import _ from "lodash";
import { Glossary, Level } from "src/hooks/useGlossary/types";
import * as GQL from "src/types/graphql";

/**
 * Helper functions
 */
function getLevel(gql: GQL.GetGlossary["glossary_by_organization"][0]): Level {
  if (gql.enrollment_period_id !== null) {
    return "enrollmentPeriod";
  }

  if (gql.organization_id !== null) {
    return "organization";
  }

  return "default";
}
function getLevelUuid(
  gql: GQL.GetGlossary["glossary_by_organization"][0]
): string {
  if (gql.enrollment_period_id !== null) {
    return gql.enrollment_period_id;
  }

  if (gql.organization_id !== null) {
    return gql.organization_id;
  }

  return "default";
}
export function fromGQL(gql: GQL.GetGlossary): Glossary {
  return gql.glossary_by_organization.reduce((acc, glossaryByOrganization) => {
    if (
      !glossaryByOrganization.glossary?.type ||
      !glossaryByOrganization.default_singular ||
      !glossaryByOrganization.default_plural ||
      !glossaryByOrganization.alias_singular ||
      !glossaryByOrganization.alias_plural
    ) {
      console.warn(`Invalid glossary: ${glossaryByOrganization}`);
      return acc;
    }
    const level = getLevel(glossaryByOrganization);
    const levelUuid = getLevelUuid(glossaryByOrganization);

    return {
      ...acc,
      [glossaryByOrganization.glossary.type]: {
        ...acc[glossaryByOrganization.glossary.type],
        [levelUuid]: {
          level: level,
          default: {
            singular: glossaryByOrganization.default_singular,
            plural: glossaryByOrganization.default_plural,
          },
          alias: {
            singular: glossaryByOrganization.alias_singular,
            plural: glossaryByOrganization.alias_plural,
          },
        },
      },
    };
  }, {} as Glossary);
}

/**
 * Replace words while preserving capitalization:
 * - first letter is uppercase, e.g.: School -> District
 * - all upper case words., e.g.: SCHOOL -> DISTRICT
 * - use all lower case for the rest
 */
export function replaceWords(
  text: string,
  words: { oldWord: string; newWord: string }[]
): string {
  if (words.length === 0) return text;

  function lowercaseFirstLetter(str: string): string {
    if (str.length === 0) {
      return str;
    }

    const firstLetter = str.charAt(0).toLowerCase();
    return firstLetter + str.slice(1);
  }

  function preserveCase(match: string, newWord: string): string {
    if (match === match.toUpperCase()) {
      return newWord.toUpperCase();
    } else if (match === match.toLowerCase()) {
      return newWord.toLowerCase();
    } else if (lowercaseFirstLetter(match) === match.toLowerCase()) {
      return newWord[0]?.toUpperCase() + newWord.slice(1);
    } else {
      return newWord.toLowerCase();
    }
  }

  // Sort by longest first so longer matches take priority over substrings.
  // Use a single regex pass to avoid cascading replacements where a
  // replacement word contains another glossary word (e.g. "pending" →
  // "pendingtest" would re-match "pending" on a second pass).
  const sorted = _.sortBy(words, (word) => -word.oldWord.length);

  const pattern = sorted.map((w) => _.escapeRegExp(w.oldWord)).join("|");
  const regex = new RegExp(pattern, "gi");

  const lookup = new Map(
    sorted.map((w) => [w.oldWord.toLowerCase(), w.newWord])
  );

  return text.replace(regex, (match) => {
    const newWord = lookup.get(match.toLowerCase()) ?? match;
    return preserveCase(match, newWord);
  });
}
